/**
 * PrebillingStatesLegend: the quiet "What the states mean" link on
 * Pre-billing, which opens a popover of state → meaning (founder feedback
 * 2026-07-10: the Admin "Billing rules" page retired; its substance lives
 * HERE, where the states actually appear, plus
 * docs/sops/billing-readiness-sop.md for the operator flow).
 *
 * LP-18: one quiet link on the row above the table, never a block of text
 * over it. The link is a TextAction (an action, not navigation); the
 * popover follows My time's Go to date (PeriodHeader): a dialog under the
 * link, closed by the link again, Escape (focus back on the link) or a
 * press outside it. Copy mirrors the semantics the server enforces
 * (BILLING_READINESS_RULES + deriveBillingReadiness + the 0135 rate gate
 * + the 0133 edit-clears-ready rule in encounterNotes.ts) — plain words,
 * no engine vocabulary (R2).
 *
 * The look (Amanda, warm and friendly within the design language): each
 * state wears the exact chip its rows wear (prebillingStateChips.ts) with
 * a house (lucide) outline icon before it, grouped under two quiet
 * headings, "Your turn" and "Nothing to do"; the rate and export notes
 * close it as a soft sage note. The popover keeps to 24rem, and on a
 * narrow screen it moves left so its right edge stays 16px inside the
 * window.
 *
 * A seat without view_invoice (a care manager) reads the same states
 * with no rate or export note, and marks its own notes Ready.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Check, Clock, Eye, Hourglass, Pencil, RefreshCw, type LucideIcon } from 'lucide-react';
import { Chip, type ChipVariant } from '@/components/primitives/Chip';
import { TextAction } from '@/components/workspace/TextAction';
import { STATE_CHIP, WAITING_CHIP } from '@/components/clientRecord/prebillingStateChips';

/** Ready's meaning for a seat without view_invoice: no download, no
 *  invoice on their screen. */
const READY_MEANING_CARE_MANAGER = 'A person confirmed this note is ready to bill.';

interface LegendState {
  chip: { label: string; variant: ChipVariant };
  icon: LucideIcon;
  meaning: string;
}

/** The states that ask something of the reader. */
const YOUR_TURN: readonly LegendState[] = [
  {
    chip: STATE_CHIP.needs_your_ok,
    icon: Eye,
    meaning:
      'The invoice line is written. Check it, then mark it Ready. No-charge visits wait here too, so they still show on the invoice.',
  },
  {
    chip: STATE_CHIP.needs_detail,
    icon: Pencil,
    meaning: 'Add what was done, or write the invoice summary yourself, and it can be billed.',
  },
  {
    chip: STATE_CHIP.failed,
    icon: RefreshCw,
    meaning: "This note couldn't be read, so it isn't on the invoice yet. Open it in Notes & activity and press Try again.",
  },
];

/** The states that ask nothing of the reader. */
const NOTHING_TO_DO: readonly LegendState[] = [
  {
    chip: STATE_CHIP.ready,
    icon: Check,
    meaning: 'Confirmed. This line goes on the invoice and the billing download.',
  },
  {
    chip: WAITING_CHIP,
    icon: Clock,
    meaning: "Another care manager's note. It goes on the invoice once they mark it Ready.",
  },
  {
    chip: STATE_CHIP.still_reading,
    icon: Hourglass,
    meaning: 'This note is still being read. Check back in a minute.',
  },
];

/** One group: its quiet heading, then each state's icon and chip with
 *  its meaning under it. */
function LegendGroup({
  heading,
  states,
  officeSeat,
  testId,
}: {
  heading: string;
  states: readonly LegendState[];
  officeSeat: boolean;
  testId: string;
}): ReactNode {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <h3 id={headingId} className="text-label font-semibold uppercase tracking-[0.16em] text-sage-400">
        {heading}
      </h3>
      <dl className="mt-2.5 space-y-3">
        {states.map(({ chip, icon: Icon, meaning }) => (
          <div key={chip.label}>
            <dt className="flex items-center gap-2">
              <Icon size={15} strokeWidth={1.75} aria-hidden="true" className="shrink-0 text-sage-600" />
              <Chip variant={chip.variant}>{chip.label}</Chip>
            </dt>
            <dd className="mt-1 pl-[23px] text-xs leading-relaxed text-ink-soft">
              {!officeSeat && chip.label === STATE_CHIP.ready.label ? READY_MEANING_CARE_MANAGER : meaning}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** The link's words. */
export const STATES_LINK = 'What the states mean';

export function PrebillingStatesLegend({
  officeSeat,
}: {
  /** The viewer holds view_invoice: the rate and export notes show. */
  officeSeat: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  const popoverRef = useRef<HTMLDivElement>(null);
  // On a narrow screen the popover moves left until its right edge is
  // 16px inside the window, so it is never cut off.
  const [shiftLeft, setShiftLeft] = useState(0);
  useLayoutEffect(() => {
    if (!open) {
      setShiftLeft(0);
      return;
    }
    const box = popoverRef.current?.getBoundingClientRect();
    const windowWidth = document.documentElement.clientWidth;
    // Nothing laid out yet (no width to measure): leave it where it opens.
    if (box === undefined || box.width === 0 || windowWidth === 0) return;
    const over = box.right - (windowWidth - 16);
    setShiftLeft(over > 0 ? Math.ceil(over) : 0);
  }, [open]);
  // Escape closes it and hands focus back to the link; a press outside
  // it closes it.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      wrapperRef.current?.querySelector<HTMLButtonElement>('[data-testid="prebilling-states-legend-toggle"]')?.focus();
      setOpen(false);
    }
    function onMouseDown(e: MouseEvent): void {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);
  return (
    <div ref={wrapperRef} className="relative">
      <TextAction
        data-testid="prebilling-states-legend-toggle"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {STATES_LINK}
      </TextAction>
      {open && (
        <div
          id={popoverId}
          ref={popoverRef}
          role="dialog"
          aria-label={STATES_LINK}
          data-testid="prebilling-states-legend"
          // The box itself moves (left, not a transform), so the page never
          // widens to fit where it would have been.
          style={shiftLeft > 0 ? { left: `-${shiftLeft}px` } : undefined}
          className="absolute left-0 top-[calc(100%+8px)] z-30 flex w-[24rem] max-w-[calc(100vw-2rem)] flex-col gap-5 rounded-card border border-sage-800/[8%] bg-paper p-5 shadow-sh3"
        >
          <LegendGroup heading="Your turn" states={YOUR_TURN} officeSeat={officeSeat} testId="prebilling-states-your-turn" />
          <LegendGroup heading="Nothing to do" states={NOTHING_TO_DO} officeSeat={officeSeat} testId="prebilling-states-nothing-to-do" />
          {officeSeat && (
            <div
              className="space-y-2 rounded-base bg-sage-50 px-3.5 py-3 text-xs leading-relaxed text-sage-800"
              data-testid="prebilling-states-note"
            >
              <p>
                To mark a note Ready, the client needs a rate for that kind of work. Changing the
                hours, rate or line sends the note back to Needs your OK, and the care manager
                marks it Ready again.
              </p>
              <p>
                A note from before native billing started shows &quot;Export only&quot;: it&apos;s in
                the billing download but never on an invoice.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
